import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react'
import { Check, ChevronsUpDown, MoreHorizontal, Plus, SlidersHorizontal } from 'lucide-react'
import clsx from 'clsx'
import { Modal, ModalHeader } from '@/components/Modal'
import { ChannelAvatar, channelProfileSource, ProfileImage } from '@/components/ChannelAvatar'
import { PLATFORMS, type PlatformId } from '@/lib/platforms'
import { uid, useStore } from '@/lib/store'
import { useUI } from '@/lib/ui'
import { activeSetOption, setOptions, type SetOption } from '@/lib/social-sets'
import { trackEvent } from '@/lib/posthog'

const MAX_NAME = 80

export function SetAvatar({ option, size, active = false }: { option: SetOption; size: number; active?: boolean }) {
  const lead = option.channels[0]
  return (
    <span className={clsx('shrink-0 rounded-full', active && 'ring-2 ring-accent ring-offset-2 ring-offset-surface')}>
      {lead ? (
        <ProfileImage
          src={channelProfileSource(lead)}
          name={option.name}
          background={lead.avatarColor || PLATFORMS[lead.platform].gradient}
          size={size}
        />
      ) : (
        <span
          className="grid place-items-center rounded-full bg-inset font-semibold text-ink-400 shadow-hairline"
          style={{ width: size, height: size, fontSize: size * 0.4 }}
        >
          {option.name.slice(0, 1).toUpperCase()}
        </span>
      )}
    </span>
  )
}

function PlatformIcons({ option }: { option: SetOption }) {
  const platforms = [...new Set(option.channels.map((c) => c.platform))] as PlatformId[]
  if (platforms.length === 0) return <span className="block text-[11px] leading-4 text-ink-400">No channels</span>
  return (
    <span className="flex h-4 items-center gap-1.5 text-ink-400" aria-label={platforms.map((p) => PLATFORMS[p].name).join(', ')}>
      {platforms.map((p) => (
        <span key={p} className="inline-flex size-3">{PLATFORMS[p].icon}</span>
      ))}
    </span>
  )
}

type EditorState = { setId: string | null; channelIds: string[]; name: string }

/** Sidebar "Posting as" control: shows the active set and switches between sets. */
export function SocialSetSwitcher() {
  const channels = useStore((s) => s.channels)
  const socialSets = useStore((s) => s.socialSets)
  const activeSetId = useStore((s) => s.activeSetId)
  const setActiveSet = useStore((s) => s.setActiveSet)
  const openConnect = useUI((s) => s.openConnect)
  const [open, setOpen] = useState(false)
  const [editor, setEditor] = useState<EditorState | null>(null)
  const popoverId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const options = useMemo(() => setOptions(socialSets, channels), [socialSets, channels])
  const active = activeSetOption(options, activeSetId)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      setOpen(false)
      triggerRef.current?.focus()
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  if (!active) {
    return (
      <button type="button" onClick={openConnect} className="flex h-9 w-full items-center gap-2 rounded-control px-2 text-left text-[12.5px] text-ink-600 transition hover:bg-hover hover:text-ink-900 active:scale-[0.98]">
        <Plus size={14} /> Connect a channel
      </button>
    )
  }

  const pick = (option: SetOption) => {
    setActiveSet(option.id)
    setOpen(false)
    trackEvent('social_set_switched', { saved: Boolean(option.set), channel_count: option.channels.length })
  }

  const edit = (option: SetOption | null) => {
    setOpen(false)
    if (!option) setEditor({ setId: null, channelIds: [], name: '' })
    else if (option.set) setEditor({ setId: option.set.id, channelIds: option.set.channelIds, name: option.set.name })
    else setEditor({ setId: null, channelIds: option.channels.map((c) => c.id), name: option.name })
  }

  return (
    <div ref={rootRef} className="relative w-full">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={popoverId}
        aria-label={`Posting as ${active.name}. Switch set`}
        className="group flex h-11 w-full items-center gap-2.5 rounded-control px-2 text-left transition hover:bg-hover active:scale-[0.98]"
      >
        <SetAvatar option={active} size={26} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[12.5px] font-medium leading-tight text-ink-900">{active.name}</span>
          <PlatformIcons option={active} />
        </span>
        <ChevronsUpDown size={14} className={clsx('shrink-0 transition', open ? 'text-ink-600' : 'text-ink-400 group-hover:text-ink-600')} />
      </button>

      {open && (
        <div
          id={popoverId}
          role="dialog"
          aria-label="Switch set"
          className="absolute bottom-[calc(100%+8px)] left-0 z-[70] w-[272px] rounded-[12px] bg-surface p-1.5 shadow-overlay animate-scale-in"
        >
          <div className="flex max-h-[min(360px,60vh)] flex-col gap-px overflow-y-auto">
            {options.map((option) => {
              const on = option.id === active.id
              return (
                <div key={option.id} className={clsx('group relative flex items-center rounded-[9px] transition', on ? 'bg-accent-tint' : 'hover:bg-hover')}>
                  <button
                    type="button"
                    onClick={() => pick(option)}
                    aria-current={on ? 'true' : undefined}
                    className="flex min-w-0 flex-1 items-center gap-2.5 px-2 py-2 text-left active:scale-[0.98]"
                  >
                    <SetAvatar option={option} size={30} active={on} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] font-medium leading-tight text-ink-900">{option.name}</span>
                      <span className="mt-1 block"><PlatformIcons option={option} /></span>
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => edit(option)}
                    aria-label={option.set ? `Edit ${option.name}` : `Make a set from ${option.name}`}
                    className={clsx(
                      'mr-1.5 grid size-7 shrink-0 place-items-center rounded-full text-ink-400 transition hover:bg-surface hover:text-ink-900 focus-visible:opacity-100 group-hover:opacity-100',
                      on ? 'opacity-100' : 'opacity-0',
                    )}
                  >
                    <MoreHorizontal size={15} />
                  </button>
                </div>
              )
            })}
          </div>
          <div className="mt-1 border-t border-line pt-1">
            <button type="button" onClick={() => edit(null)} className="flex h-9 w-full items-center gap-2.5 rounded-control px-2.5 text-[12.5px] text-ink-700 transition hover:bg-hover hover:text-ink-900">
              <Plus size={15} className="text-ink-400" /> New set
            </button>
            <button type="button" onClick={() => { setOpen(false); openConnect() }} className="flex h-9 w-full items-center gap-2.5 rounded-control px-2.5 text-[12.5px] text-ink-700 transition hover:bg-hover hover:text-ink-900">
              <SlidersHorizontal size={15} className="text-ink-400" /> Manage channels
            </button>
          </div>
        </div>
      )}

      <SocialSetModal editor={editor} onClose={() => setEditor(null)} onSaved={(id) => setActiveSet(id)} />
    </div>
  )
}

export function SocialSetModal({
  editor,
  onClose,
  onSaved,
}: {
  editor: EditorState | null
  onClose: () => void
  onSaved?: (id: string) => void
}) {
  const channels = useStore((s) => s.channels)
  const saveSocialSet = useStore((s) => s.saveSocialSet)
  const deleteSocialSet = useStore((s) => s.deleteSocialSet)
  const [name, setName] = useState('')
  const [picked, setPicked] = useState<string[]>([])
  const [busy, setBusy] = useState<'save' | 'delete' | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!editor) return
    setName(editor.name)
    setPicked(editor.channelIds)
    setBusy(null)
    setError(null)
  }, [editor])

  if (!editor) return null
  const editing = editor.setId !== null
  const valid = name.trim().length > 0 && picked.length > 0

  const toggle = (id: string) => setPicked((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]))

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!valid || busy) return
    setBusy('save')
    setError(null)
    try {
      // Keep the connected-channel order so the set's avatar is predictable.
      const channelIds = channels.map((c) => c.id).filter((id) => picked.includes(id))
      const saved = await saveSocialSet({ id: editor.setId ?? uid(), name: name.trim(), channelIds })
      trackEvent(editing ? 'social_set_updated' : 'social_set_created', { channel_count: channelIds.length })
      onSaved?.(saved.id)
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the set.')
      setBusy(null)
    }
  }

  const remove = async () => {
    if (!editor.setId || busy) return
    setBusy('delete')
    setError(null)
    try {
      await deleteSocialSet(editor.setId)
      trackEvent('social_set_deleted', {})
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete the set.')
      setBusy(null)
    }
  }

  return (
    <Modal open onClose={onClose} size="sm" ariaLabel={editing ? 'Edit set' : 'New set'}>
      <ModalHeader title={editing ? 'Edit set' : 'New set'} onClose={onClose} />
      <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <label className="block text-xs font-medium text-ink-600">
            Name
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={MAX_NAME}
              placeholder="Personal"
              autoFocus
              className="input mt-1.5"
            />
          </label>
          <p className="mb-1.5 mt-4 text-xs font-medium text-ink-600">Channels</p>
          <div className="-mx-2 flex flex-col gap-px">
            {channels.map((c) => {
              const on = picked.includes(c.id)
              return (
                <label key={c.id} className="flex cursor-pointer items-center gap-2.5 rounded-control px-2 py-1.5 transition hover:bg-hover">
                  <ChannelAvatar channel={c} size="sm" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium text-ink-900">{c.name}</span>
                    <span className="block truncate text-[11px] text-ink-400">
                      {PLATFORMS[c.platform].name}
                      {c.handle ? ` · ${c.handle}` : ''}
                    </span>
                  </span>
                  <input type="checkbox" checked={on} onChange={() => toggle(c.id)} className="peer sr-only" />
                  <span
                    aria-hidden
                    className={clsx(
                      'grid size-[18px] shrink-0 place-items-center rounded-full transition peer-focus-visible:ring-2 peer-focus-visible:ring-accent/40',
                      on ? 'bg-accent text-white' : 'bg-inset text-transparent shadow-hairline',
                    )}
                  >
                    <Check size={11} strokeWidth={2.8} />
                  </span>
                </label>
              )
            })}
          </div>
        </div>
        {error && <p className="px-5 pb-2 text-xs text-red" role="alert">{error}</p>}
        <div className="flex items-center gap-2 border-t border-line px-5 py-3">
          {editing && (
            <button type="button" onClick={() => void remove()} disabled={busy !== null} className="btn-subtle -ml-3 text-red hover:text-red">
              {busy === 'delete' ? 'Deleting...' : 'Delete set'}
            </button>
          )}
          <span className="ml-auto" />
          <button type="button" onClick={onClose} className="btn-subtle">Cancel</button>
          <button type="submit" disabled={!valid || busy !== null} className="btn-primary">
            {busy === 'save' ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
